import React, { useContext, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { MDropdown } from 'common';

import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from 'authentication/AuthContext';
import * as R from 'ramda';
import { arrayOf, bool, func, shape } from 'prop-types';

// eslint-disable-next-line sort-keys-fix/sort-keys-fix
const filterStyle = { maxWidth: { xs: 'initial', lg: 250 }, width: 1 };

const renderInput = R.curry((label, params) => (
  <TextField
    {...params}
    label={label}
    slotProps={{
      htmlInput: { ...params.inputProps },

      input: {
        ...params.InputProps,
        endAdornment: params.InputProps.endAdornment,
      },
    }}
  />
));

const FilterPanel = ({ allocationGroups, filters, isFetching, productGroups, setFilters }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const navigate = useNavigate();
  const { view } = useParams();
  const { currentAffiliate } = useUserRolePermissions();
  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);
  const affiliatesOptions = useGetAffiliateOptions(['inventoryOwner']);
  const allocationGroup = R.defaultTo(null, R.prop('allocationGroup', filters));
  const productGroup = R.defaultTo(null, R.prop('productGroup', filters));
  const [collapseOpen, setCollapseOpen] = useState(true);

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    setCurrentSelectedAffiliate(affiliateIndex);
    navigate(
      `/affiliate/${affiliate?.customerIdName}/${affiliate?.dataCompanyId}/inventory-management/inventory-dashboard/${view}`,
      {
        replace: true,
      }
    );
  };

  const handlePipelineChange = val => {
    setFilters({
      ...filters,
      allocationGroup: val,
      productGroup: null,
    });
  };

  const FilterGroup = () => (
    <>
      {affiliatesOptions.length > 1 && (
        <Box sx={filterStyle}>
          <MDropdown
            id="affiliate-select"
            items={affiliatesOptions}
            label="Owner"
            name="affiliate"
            onChange={({ value }) => handleAffiliateChange(value)}
            value={`${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`}
          />
        </Box>
      )}
      <Autocomplete
        getOptionLabel={o => R.defaultTo('', R.prop('groupName', o))}
        isOptionEqualToValue={R.eqProps('groupId')}
        loading={isFetching}
        onChange={R.flip(handlePipelineChange)}
        options={allocationGroups}
        renderInput={renderInput('Pipeline System')}
        sx={filterStyle}
        value={allocationGroup}
      />
      <Autocomplete
        disabled={R.isNil(R.prop('groupId', allocationGroup))}
        getOptionLabel={o => R.defaultTo('', R.prop('groupName', o))}
        isOptionEqualToValue={R.eqProps('groupId')}
        loading={isFetching}
        onChange={R.compose(setFilters, R.assoc('productGroup'), R.nthArg(1))}
        options={productGroups}
        renderInput={renderInput('Product Group')}
        sx={filterStyle}
        value={productGroup}
      />
    </>
  );

  return lgUp ? (
    <Stack alignItems="center" direction="row" spacing={2}>
      <FilterGroup />
    </Stack>
  ) : (
    <>
      <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
        <Stack alignItems="center" direction="column" spacing={2}>
          <FilterGroup />
        </Stack>
      </Collapse>
      <Button
        onClick={() => setCollapseOpen(!collapseOpen)}
        sx={{
          backgroundColor: 'transparent',
          mt: collapseOpen ? 2 : 0,
          px: 0,
          py: 1,
        }}
        variant="text"
      >
        {collapseOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
        {collapseOpen ? 'Collapse Filters' : 'Expand Filters'}
      </Button>
    </>
  );
};

FilterPanel.propTypes = {
  allocationGroups: arrayOf(shape({})).isRequired,
  filters: shape({}).isRequired,
  isFetching: bool.isRequired,
  productGroups: arrayOf(shape({})).isRequired,
  setFilters: func.isRequired,
};

export default FilterPanel;
